"use client";

import { useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Propriétés typées de l'objet `user`, telles que TypeScript les connaît.
const PROPERTIES = [
    { name: "name", kind: "string", badge: "abc", bg: "bg-sky-500/15", color: "text-sky-300" },
    { name: "age", kind: "number", badge: "123", bg: "bg-amber-500/15", color: "text-amber-300" },
    { name: "email", kind: "string", badge: "abc", bg: "bg-sky-500/15", color: "text-sky-300" },
    { name: "isActive", kind: "boolean", badge: "T/F", bg: "bg-emerald-500/15", color: "text-emerald-300" },
];

export default function TypescriptAutocomplete() {
    const t = useTranslations();
    const reduce = useReducedMotion();
    const [selected, setSelected] = useState(null);

    return (
        <section id="typescript-autocomplete" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Ts_auto_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Ts_auto_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows (teinte TypeScript) */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-sky-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:typescript-icon"
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        user.ts
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Ts_auto_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7 grid lg:grid-cols-2 gap-5 items-start">
                    {/* Éditeur */}
                    <div className="min-w-0 rounded-xl bg-black/30 ring-1 ring-white/10 p-4 font-mono text-[12.5px] sm:text-[13px] leading-relaxed">
                        <div>
                            <span className="text-fuchsia-400">type </span>
                            <span className="text-blue-300">User</span>
                            <span className="text-gray-400"> = {"{"}</span>
                        </div>
                        {PROPERTIES.map((p) => (
                            <div key={p.name} className="pl-4">
                                <span className="text-sky-200">{p.name}</span>
                                <span className="text-gray-400">: </span>
                                <span className="text-blue-300">{p.kind}</span>
                                <span className="text-gray-400">;</span>
                            </div>
                        ))}
                        <div>
                            <span className="text-gray-400">{"};"}</span>
                        </div>
                        <div>&nbsp;</div>
                        <div>
                            <span className="text-fuchsia-400">const </span>
                            <span className="text-sky-200">user</span>
                            <span className="text-gray-400">: </span>
                            <span className="text-blue-300">User</span>
                            <span className="text-gray-400"> = </span>
                            <span className="text-sky-400">chargerUser</span>
                            <span className="text-gray-400">();</span>
                        </div>
                        <div>&nbsp;</div>

                        {/* Ligne interactive */}
                        <div className="relative">
                            <span className="text-sky-200">user</span>
                            <span className="text-gray-400">.</span>
                            {selected ? (
                                <>
                                    <span className="text-sky-200">
                                        {selected.name}
                                    </span>
                                    <span className="text-gray-500">
                                        {"  // "}
                                        {selected.kind}
                                    </span>
                                </>
                            ) : (
                                <span className="inline-block w-[2px] h-[15px] align-middle bg-blue-400 animate-pulse" />
                            )}

                            {/* Menu d'autocomplétion */}
                            {!selected && (
                                <motion.div
                                    initial={
                                        reduce ? false : { opacity: 0, y: -4 }
                                    }
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="mt-1.5 w-[270px] max-w-full rounded-lg bg-bgDark-900 ring-1 ring-white/15 shadow-2xl overflow-hidden"
                                >
                                    {PROPERTIES.map((p, i) => (
                                        <button
                                            key={p.name}
                                            type="button"
                                            onClick={() => setSelected(p)}
                                            className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors hover:bg-blue-500/25 ${
                                                i === 0 ? "bg-blue-500/15" : ""
                                            }`}
                                        >
                                            <span
                                                className={`flex items-center justify-center w-7 h-5 shrink-0 rounded text-[9px] font-bold ${p.bg} ${p.color}`}
                                            >
                                                {p.badge}
                                            </span>
                                            <span className="text-[13px] text-white">
                                                {p.name}
                                            </span>
                                            <span className="ml-auto text-[12px] text-gray-500">
                                                {p.kind}
                                            </span>
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </div>
                    </div>

                    {/* Panneau explicatif */}
                    <div className="min-w-0 flex flex-col gap-4">
                        <div className="flex items-start gap-3 rounded-xl bg-white/[0.04] ring-1 ring-white/10 p-4">
                            <SolarIcon
                                name="magic-stick-3-bold"
                                className="w-5 h-5 text-blue-300 shrink-0 mt-0.5"
                            />
                            <p className="text-sm text-gray-300 leading-relaxed mb-0">
                                {t("Ts_auto_hint")}
                            </p>
                        </div>

                        {selected && (
                            <motion.div
                                key={selected.name}
                                initial={reduce ? false : { opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.25 }}
                                className="rounded-xl bg-blue-500/[0.08] ring-1 ring-blue-400/30 p-4"
                            >
                                <div className="flex items-center gap-2 mb-2 font-mono text-[13px]">
                                    <span className="text-sky-200">user</span>
                                    <span className="text-gray-400">.</span>
                                    <span className="text-sky-200">
                                        {selected.name}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-[10px] uppercase tracking-[0.16em] text-gray-500">
                                        {t("Ts_auto_inferred_label")}
                                    </span>
                                    <span
                                        className={`rounded px-2 py-0.5 font-mono text-[12px] ${selected.bg} ${selected.color}`}
                                    >
                                        {selected.kind}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setSelected(null)}
                                    className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-white/10 px-3 py-1.5 text-xs text-gray-300 transition active:scale-95"
                                >
                                    <SolarIcon
                                        name="restart-bold-duotone"
                                        className="w-3.5 h-3.5"
                                    />
                                    {t("Ts_auto_retry")}
                                </button>
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Ts_auto_caption")}
            </p>
        </section>
    );
}
